import React, { useState, useEffect } from 'react'
import { usePatient } from 'hooks/useURL'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import debug from 'Helper/debugHelpers'
import { releaseLock, acquireLock } from 'actions/lockActions'
import lockLookupTable from 'Helper/lockLookupTable'

const lockable = (priv, privOperator) => {
  return WrappedComponent => {
    const Lockable = props => {
      const dispatch = useDispatch()
      const locks = useSelector(state => state.locks.patient)
      const username = useSelector(state => state.auth.username)

      const [myLockUntil, setMyLockUntil] = useState('unlockable')
      const [pendingLockRequest, setPendingLockRequest] = useState(false)

      const {
        windowConfig,
        hasPrivileges
      } = props

      const patientId = usePatient()

      useEffect(() => {
        return () => {
          // component will unmount
          debug.log(
            'Releasing window lock for ' + windowConfig.ref,
            debug.logLevels.WINDOW_LOCK
          )
          dispatch(releaseLock(windowConfig.ref))
        }
      }, [])

      useEffect(() => {
        if (!hasPrivileges) {
          return
        }
        const lockType = lockLookupTable(windowConfig.ref)
        const relevantLock = locks.find(lock => {
          return lock.type === lockType && patientId == lock.patient_id
        })

        // this window requires a lock (lockType) and a request
        // isn't already in progress...
        if (!pendingLockRequest && lockType != null) {
          if (relevantLock != null) {
            if (
              relevantLock.username === username &&
              patientId != null &&
              relevantLock.patient_id != patientId
            ) {
              // we changed patients so we need to release the old lock
              // and get the new one.
              dispatch(releaseLock(windowConfig.ref))
              dispatch(acquireLock(windowConfig.ref, patientId))
              setPendingLockRequest(true)
            } else if (
              relevantLock.username === username &&
              moment(relevantLock.locked_until, 'YYYY-MM-DD HH:mm:ss').isBefore(
                moment().add(1, 'minutes')
              )
            ) {
              // I own the lock and the expiration is within 1 minute so
              // refresh it.
              debug.log(
                'Refreshing window lock for ' + windowConfig.ref,
                debug.logLevels.WINDOW_LOCK
              )
              dispatch(acquireLock(windowConfig.ref, patientId))
              setPendingLockRequest(true)
            }
          } else {
            // no one has a lock yet so I should try to grab it.
            debug.log(
              'Acquiring window lock for ' + windowConfig.ref,
              debug.logLevels.WINDOW_LOCK
            )
            dispatch(acquireLock(windowConfig.ref, patientId))
            setPendingLockRequest(true)
          }
        }

        // manage state.myLockUntil for the next update.
        if (relevantLock != null) {
          if (
            relevantLock.username === username &&
            (myLockUntil === null || myLockUntil === 'unlockable')
          ) {
            // we own the lock so we allow full access to the window
            setMyLockUntil(relevantLock.locked_until)
            setPendingLockRequest(false)
          } else if (relevantLock.username !== username) {
            // someone else owns the lock, so the window is locked out. Any
            // request of ours is over even if it was refused, so clear the
            // pending flag whether or not the window was already locked out.
            // Otherwise a refused request leaves it set, and the window never
            // asks again when this lock later expires.
            setPendingLockRequest(false)
            setMyLockUntil(null)
          } else if (
            relevantLock.username === username &&
            pendingLockRequest &&
            myLockUntil !== relevantLock.locked_until
          ) {
            // lock request is no longer pending but it didn't change
            // the state (no one had the lock before and we have it now)
            // so both before and after it looks unlocked to us.
            setPendingLockRequest(false)
            setMyLockUntil(relevantLock.locked_until)
          }
        } else if (myLockUntil === null) {
          // no one holds the lock any more, so the lock out is over. The
          // request dispatched above takes the lock if we are still free to.
          setMyLockUntil('unlockable')
        }
      }, [windowConfig.ref, locks, username, patientId, hasPrivileges])

      return (
        <WrappedComponent
          isLocked={myLockUntil === null}
          {...props}
        />
      )
    }
    return Lockable
  }
}

export default lockable

lockable.propTypes = {
  // The match retured by redux router used to determine patientId to auto pass
  // to windows and also to determine if window is in workflow state
  match: PropTypes.object,
  // WindowConfig passed in by WindowContainer
  windowConfig: PropTypes.object.isRequired,
  // Optional window title to override what is in the window config
  windowTitle: PropTypes.string
}
